import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ProgressBar from '../components/ProgressBar';
import { validateStep3Famille } from '../utils/validationUtils';
import localforage from 'localforage';

export default function Step3() {
  const navigate = useNavigate();

  // Fonction pour calculer l'âge à partir de la date de naissance
  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  };

  // Fonction pour valider l'âge d'un enfant
  // Retourne null si l'âge est valide (<=18), ou un objet avec message et requireAttestation si > 18
  const validateChildAge = (dob) => {
    const age = calculateAge(dob);
    if (age === null) return null;

    if (age > 18) {
      return {
        message: `L'enfant a dépassé l'âge limite de 18 ans. Veuillez joindre une attestation de fréquentation pour poursuivre la souscription.`,
        requireAttestation: true,
        age
      };
    }

    return null;
  };

  const [hasSpouse, setHasSpouse] = useState(false);
  // const [spouse, setSpouse] = useState({ nom: '', prenoms: '', dob: '', lienParente: 'conjoint', sexe: '', cniRecto: null, cniVerso: null, nni: '', photo: null, carteCmu: null });
  const [spouse, setSpouse] = useState({ nom: '', prenoms: '', dob: '', lienParente: 'conjoint', sexe: '', nni: '' });
  const [numberOfChildren, setNumberOfChildren] = useState(0);
  const [children, setChildren] = useState([]);
  const [errors, setErrors] = useState({});
  const [spousePreviews, setSpousePreviews] = useState({ cniRecto: null, cniVerso: null, photo: null, carteCmu: null });
  const [spouseFiles, setSpouseFiles] = useState({ cniRecto: null, cniVerso: null, photo: null, carteCmu: null });
  const [childrenPreviews, setChildrenPreviews] = useState([]);
  const [childrenFiles, setChildrenFiles] = useState([]);
  const [assuranceType, setAssuranceType] = useState(null);
  // Type de document CMU pour le conjoint
  const [spouseCmuDocType, setSpouseCmuDocType] = useState('carte');
  // Types de document CMU pour chaque enfant
  const [childrenCmuDocType, setChildrenCmuDocType] = useState([]);
  const MONO_FAMILLE_TYPE_UUID = '019c2845-b537-7d9c-b921-5d56e6002593';

  useEffect(() => {
    // Vérifier que le type est bien FAMILLE ou MONO_FAMILLE, sinon rediriger
    const assuredType = localStorage.getItem('assuredType');
    const SOLO_TYPE_UUID = '019beade-d8aa-71da-85e4-afa966b97457';

    if (assuredType === SOLO_TYPE_UUID) {
      // Si c'est SOLO, rediriger vers Step4
      navigate('/step-4');
      return;
    }

    // Charger le type d'assurance
    setAssuranceType(assuredType);

    // Charger les données sauvegardées
    const savedHasSpouse = localStorage.getItem('hasSpouse') === 'true';
    const savedSpouse = JSON.parse(localStorage.getItem('spouse') || '{}');
    const savedNumberOfChildren = parseInt(localStorage.getItem('numberOfChildren')) || 0;
    const savedChildren = JSON.parse(localStorage.getItem('children') || '[]');

    // Si c'est FAMILLE, forcer hasSpouse à true
    // Si c'est MONO_FAMILLE, forcer hasSpouse à false
    let finalHasSpouse = savedHasSpouse;
    if (assuredType === '019beade-f87c-73ec-a06f-9d24a4e4dc80') {
      // FAMILLE_TYPE_UUID
      finalHasSpouse = true;
    } else if (assuredType === '019c2845-b537-7d9c-b921-5d56e6002593') {
      // MONO_FAMILLE_TYPE_UUID
      finalHasSpouse = false;
    }

    setHasSpouse(finalHasSpouse);
    if (Object.keys(savedSpouse).length > 0) setSpouse(savedSpouse);
    setNumberOfChildren(savedNumberOfChildren);
    if (savedChildren.length > 0) setChildren(savedChildren);

    // Initialiser les types CMU enfants
    setChildrenCmuDocType(Array.from({ length: savedNumberOfChildren }, () => 'carte'));
  }, [navigate]);

  const handleContinue = async (e) => {
    e.preventDefault();

    // Validation de l'âge des enfants
    if (numberOfChildren > 0) {
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        if (child.dob) {
          const ageResult = validateChildAge(child.dob);
          if (ageResult && ageResult.requireAttestation) {
            // Vérifier que l'attestation de fréquentation est fournie
            if (!child.attestationFrequentation) {
              setErrors(prev => ({
                ...prev,
                [`childDob${i}`]: `L'enfant a plus de 18 ans. Une attestation de fréquentation est requise pour continuer.`
              }));
              return;
            }
          }
        }
      }
    }

    const validation = validateStep3Famille(hasSpouse, spouse, numberOfChildren, children);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setErrors({});
    localStorage.setItem('hasSpouse', hasSpouse.toString());
    localStorage.setItem('spouse', JSON.stringify(spouse));
    localStorage.setItem('numberOfChildren', numberOfChildren.toString());
    localStorage.setItem('children', JSON.stringify(children));
    await localforage.setItem('spouseFiles', spouseFiles);
    await localforage.setItem('childrenFiles', childrenFiles);

    navigate('/step-4');
  };

  return (
    <div className="subscribe py-10 lg:py-16 bg-[#e4eef9]">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <h1 className="text-center text-4xl font-bold">Formulaire de souscription</h1>
        <div className="mt-10">
          <ProgressBar currentStep={3} />
          <div className="mt-8 bg-white p-6 lg:p-10 rounded-xl shadow-sm">
            <h2 className="text-xl font-bold text-secondary mb-6">3. Informations de la famille</h2>
            <form className="space-y-4">
              {/* Question du conjoint - seulement pour les types où le conjoint est optionnel */}
              {assuranceType !== MONO_FAMILLE_TYPE_UUID && assuranceType !== '019beade-f87c-73ec-a06f-9d24a4e4dc80' && (
                <div className="pt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={hasSpouse} onChange={(e) => { setHasSpouse(e.target.checked); localStorage.setItem('hasSpouse', e.target.checked.toString()); }} className="h-5 w-5" />
                    Voulez-vous assurer votre conjoint(e) ?
                  </label>
                </div>
              )}

              {/* Afficher le titre du conjoint pour FAMILLE (où c'est obligatoire) */}
              {assuranceType === '019beade-f87c-73ec-a06f-9d24a4e4dc80' && (
                <div className="pt-2 bg-blue-50 p-4 rounded-lg">
                  <p className="text-gray-700">Pour l'offre <strong>Famille</strong>, le conjoint(e) est obligatoire.</p>
                </div>
              )}
              <div>
                <label className="block font-bold mb-2">Nombre d'enfant(s) à assurer</label>
                <input
                  type="number"
                  min="0"
                  value={numberOfChildren}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    setNumberOfChildren(val);
                    localStorage.setItem('numberOfChildren', val.toString());
                    const newChildren = Array.from({ length: val }, (_, index) => {
                      return children[index] || { nom: '', prenoms: '', dob: '', lienParente: 'enfant', sexe: '', cniExtrait: null, nni: '', photo: null, carteCmu: null, attestationFrequentation: null };
                    });
                    setChildren(newChildren);
                    // Initialiser les previews pour les nouveaux enfants
                    const newPreviews = Array.from({ length: val }, (_, index) => {
                      return childrenPreviews[index] || { cniExtrait: null, photo: null, carteCmu: null, attestationFrequentation: null };
                    });
                    setChildrenPreviews(newPreviews);
                    // Initialiser les types CMU
                    setChildrenCmuDocType(Array.from({ length: val }, (_, index) => {
                      return childrenCmuDocType[index] || 'carte';
                    }));
                  }}
                  className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none"
                  placeholder="Nombre d'enfant"
                />
              </div>

              {hasSpouse && (
                <div>
                  <h3 className="font-bold mb-4">Informations du conjoint</h3>
                  <div className="mb-6 p-4 border rounded">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block font-bold mb-2">NOM</label>
                        <input type="text" value={spouse.nom} onChange={(e) => { const newSpouse = {...spouse, nom: e.target.value}; setSpouse(newSpouse); localStorage.setItem('spouse', JSON.stringify(newSpouse)); }} className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none" />
                      </div>
                      <div>
                        <label className="block font-bold mb-2">PRÉNOM(S)</label>
                        <input type="text" value={spouse.prenoms} onChange={(e) => { const newSpouse = {...spouse, prenoms: e.target.value}; setSpouse(newSpouse); localStorage.setItem('spouse', JSON.stringify(newSpouse)); }} className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none" />
                      </div>
                      <div>
                        <label className="block font-bold mb-2">DATE DE NAISSANCE</label>
                        <input type="date" value={spouse.dob} onChange={(e) => { const newSpouse = {...spouse, dob: e.target.value}; setSpouse(newSpouse); localStorage.setItem('spouse', JSON.stringify(newSpouse)); if (errors.spouseDob) setErrors({...errors, spouseDob: ''}); }} className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none" />
                        {errors.spouseDob && <p className="text-red-500 text-sm mt-1">{errors.spouseDob}</p>}
                      </div>
                      <div>
                        <label className="block font-bold mb-2">LIEN DE PARENTÉ</label>
                        <input type="text" value={spouse.lienParente || 'Conjoint(e)'} readOnly className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 bg-gray-100 focus:border-primary focus:outline-none" />
                      </div>
                      <div>
                        <label className="block font-bold mb-2">SEXE</label>
                        <select value={spouse.sexe} onChange={(e) => { const newSpouse = {...spouse, sexe: e.target.value}; setSpouse(newSpouse); localStorage.setItem('spouse', JSON.stringify(newSpouse)); }} className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none">
                          <option value="">Sélectionner</option>
                          <option value="M">Masculin</option>
                          <option value="F">Féminin</option>
                        </select>
                      </div>
                      <div>
                        <label className="block font-bold mb-2">N° NNI</label>
                        <input type="text" value={spouse.nni || ''} onChange={(e) => { const newSpouse = {...spouse, nni: e.target.value}; setSpouse(newSpouse); localStorage.setItem('spouse', JSON.stringify(newSpouse)); }} className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none" />
                      </div>
                      <div>
                        <label className="block font-bold mb-2">PIECE RECTO</label>
                        <input type="file" accept="image/*" onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            // const reader = new FileReader();
                            // reader.onloadend = () => {
                            //   const newSpouse = {...spouse, cniRecto: reader.result};
                            //   setSpouse(newSpouse);
                            //   setSpousePreviews({...spousePreviews, cniRecto: reader.result});
                            //   localStorage.setItem('spouse', JSON.stringify(newSpouse));
                            // };
                            // reader.readAsDataURL(file);

                            const previewUrl = URL.createObjectURL(file);
                            const newFile = {...spouseFiles, cniRecto: file};
                            setSpouseFiles(newFile);
                            setSpousePreviews({...spousePreviews, cniRecto: previewUrl});
                            localforage.setItem('spouseFiles', newFile);
                          } 
                        }} className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none" />
                        {spousePreviews.cniRecto && <img src={spousePreviews.cniRecto} alt="Aperçu CNI Recto" className="mt-2 h-20 object-contain" />}
                      </div>
                      <div>
                        <label className="block font-bold mb-2">PIECE VERSO</label>
                        <input type="file" accept="image/*" onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            // const reader = new FileReader();
                            // reader.onloadend = () => {
                            //   const newSpouse = {...spouse, cniVerso: reader.result};
                            //   setSpouse(newSpouse);
                            //   setSpousePreviews({...spousePreviews, cniVerso: reader.result});
                            //   localStorage.setItem('spouse', JSON.stringify(newSpouse));
                            // };
                            // reader.readAsDataURL(file);

                            const previewUrl = URL.createObjectURL(file);
                            const newFile = {...spouseFiles, cniVerso: file};
                            setSpouseFiles(newFile);
                            setSpousePreviews({...spousePreviews, cniVerso: previewUrl});
                            localforage.setItem('spouseFiles', newFile);
                          }
                        }} className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none" />
                        {spousePreviews.cniVerso && <img src={spousePreviews.cniVerso} alt="Aperçu CNI Verso" className="mt-2 h-20 object-contain" />}
                      </div>
                      <div>
                        <label className="block font-bold mb-2">PHOTO</label>
                        <input type="file" accept="image/*" onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            // const reader = new FileReader();
                            // reader.onloadend = () => {
                            //   const newSpouse = {...spouse, photo: reader.result};
                            //   setSpouse(newSpouse);
                            //   setSpousePreviews({...spousePreviews, photo: reader.result});
                            //   localStorage.setItem('spouse', JSON.stringify(newSpouse));
                            // };
                            // reader.readAsDataURL(file);

                            const previewUrl = URL.createObjectURL(file);
                            const newFile = {...spouseFiles, photo: file};
                            setSpouseFiles(newFile);
                            setSpousePreviews({...spousePreviews, photo: previewUrl});
                            localforage.setItem('spouseFiles', newFile);
                          }
                        }} className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none" />
                        {spousePreviews.photo && <img src={spousePreviews.photo} alt="Aperçu" className="mt-2 h-20 object-contain" />}
                      </div>
                      <div>
                        <label className="block font-bold mb-2">DOCUMENT CMU</label>
                        <div className="flex gap-4 mb-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="spouseCmuDocType"
                              value="carte"
                              checked={spouseCmuDocType === 'carte'}
                              onChange={() => setSpouseCmuDocType('carte')}
                            />
                            <span>Carte CMU</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="spouseCmuDocType"
                              value="recu"
                              checked={spouseCmuDocType === 'recu'}
                              onChange={() => setSpouseCmuDocType('recu')}
                            />
                            <span>Reçu d'enrôlement</span>
                          </label>
                        </div>
                        <input type="file" accept="image/*" onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            // const reader = new FileReader();
                            // reader.onloadend = () => {
                            //   const newSpouse = {...spouse, carteCmu: reader.result};
                            //   setSpouse(newSpouse);
                            //   setSpousePreviews({...spousePreviews, carteCmu: reader.result});
                            //   localStorage.setItem('spouse', JSON.stringify(newSpouse));
                            // };
                            // reader.readAsDataURL(file);

                            const previewUrl = URL.createObjectURL(file);
                            const newFile = {...spouseFiles, carteCmu: file};
                            setSpouseFiles(newFile);
                            setSpousePreviews({...spousePreviews, carteCmu: previewUrl});
                            localforage.setItem('spouseFiles', newFile);
                          }
                        }} className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none" />
                        {spousePreviews.carteCmu && (
                          <img
                            src={spousePreviews.carteCmu}
                            alt={spouseCmuDocType === 'recu' ? "Reçu d'enrôlement" : "Carte CMU"}
                            className="mt-2 h-20 object-contain"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {numberOfChildren > 0 && (
                <div>
                  <h3 className="font-bold mb-4">Informations des enfants</h3>
                  {children.map((child, index) => {
                    const ageResult = child.dob ? validateChildAge(child.dob) : null;
                    const childOverAge = ageResult && ageResult.requireAttestation;

                    return (
                      <div key={index} className="mb-6 p-4 border rounded">
                        <h4 className="font-bold mb-2">Enfant {index + 1}</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block font-bold mb-2">NOM</label>
                            <input type="text" value={child.nom} onChange={(e) => {
                              const newChildren = [...children];
                              newChildren[index].nom = e.target.value;
                              setChildren(newChildren);
                              localStorage.setItem('children', JSON.stringify(newChildren));
                            }} className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none" />
                          </div>
                          <div>
                            <label className="block font-bold mb-2">PRÉNOM(S)</label>
                            <input type="text" value={child.prenoms} onChange={(e) => {
                              const newChildren = [...children];
                              newChildren[index].prenoms = e.target.value;
                              setChildren(newChildren);
                              localStorage.setItem('children', JSON.stringify(newChildren));
                            }} className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none" />
                          </div>
                          <div>
                            <label className="block font-bold mb-2">DATE DE NAISSANCE</label>
                            <input type="date" value={child.dob} onChange={(e) => {
                              const newValue = e.target.value;
                              const newChildren = [...children];
                              newChildren[index].dob = newValue;
                              // Réinitialiser l'attestation si l'âge repasse en dessous de 18
                              const newAgeResult = validateChildAge(newValue);
                              if (!newAgeResult || !newAgeResult.requireAttestation) {
                                newChildren[index].attestationFrequentation = null;
                                const newPreviews = [...childrenPreviews];
                                if (newPreviews[index]) newPreviews[index].attestationFrequentation = null;
                                setChildrenPreviews(newPreviews);
                              }
                              setChildren(newChildren);
                              localStorage.setItem('children', JSON.stringify(newChildren));
                              if (errors[`childDob${index}`]) {
                                setErrors(prev => ({...prev, [`childDob${index}`]: ''}));
                              }
                            }} className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none" />
                            {childOverAge && (
                              <div className="mt-2 p-3 bg-amber-50 border border-amber-300 rounded-lg">
                                <p className="text-amber-700 text-sm font-medium">{ageResult.message}</p>
                              </div>
                            )}
                            {errors[`childDob${index}`] && <p className="text-red-500 text-sm mt-1">{errors[`childDob${index}`]}</p>}
                          </div>
                          <div>
                            <label className="block font-bold mb-2">LIEN DE PARENTÉ</label>
                            <input type="text" value="Enfant" readOnly className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 bg-gray-100 focus:border-primary focus:outline-none" />
                          </div>
                          <div>
                            <label className="block font-bold mb-2">SEXE</label>
                            <select value={child.sexe} onChange={(e) => {
                              const newChildren = [...children];
                              newChildren[index].sexe = e.target.value;
                              setChildren(newChildren);
                              localStorage.setItem('children', JSON.stringify(newChildren));
                            }} className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none">
                              <option value="">Sélectionner</option>
                              <option value="M">Masculin</option>
                              <option value="F">Féminin</option>
                            </select>
                          </div>
                          <div>
                            <label className="block font-bold mb-2">N° NNI</label>
                            <input type="text" value={child.nni || ''} onChange={(e) => {
                              const newChildren = [...children];
                              newChildren[index].nni = e.target.value;
                              setChildren(newChildren);
                              localStorage.setItem('children', JSON.stringify(newChildren));
                            }} className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none" />
                          </div>
                          <div>
                            <label className="block font-bold mb-2">PIECE D'IDENTITE OU EXTRAIT DE NAISSANCE</label>
                            <input type="file" accept="image/*" onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                // const reader = new FileReader();
                                // reader.onloadend = () => {
                                //   const newChildren = [...children];
                                //   newChildren[index].cniExtrait = reader.result;
                                //   setChildren(newChildren);
                                //   const newPreviews = [...childrenPreviews];
                                //   if (!newPreviews[index]) newPreviews[index] = {};
                                //   newPreviews[index].cniExtrait = reader.result;
                                //   setChildrenPreviews(newPreviews);
                                //   localStorage.setItem('children', JSON.stringify(newChildren));
                                // };
                                // reader.readAsDataURL(file);

                                const previewUrl = URL.createObjectURL(file); 

                                const newFiles = [...childrenFiles];
                                if (!newFiles[index]) {
                                  newFiles[index] = {};
                                } 
                                if (newFiles[index].cniExtrait) {
                                  URL.revokeObjectURL(newFiles[index].cniExtrait);
                                }
                                newFiles[index].cniExtrait = file;
                                setChildrenFiles(newFiles);

                                const newPreviews = [...childrenPreviews];
                                if (!newPreviews[index]) {
                                  newPreviews[index] = {};
                                } 
                                if (newPreviews[index].cniExtrait) {
                                  URL.revokeObjectURL(newPreviews[index].cniExtrait);
                                }
                                newPreviews[index].cniExtrait = previewUrl;
                                setChildrenPreviews(newPreviews);

                                localforage.setItem('childrenFiles', newFiles);
                              } 
                            }} className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none" />
                            {childrenPreviews[index]?.cniExtrait && <img src={childrenPreviews[index].cniExtrait} alt="Aperçu CNI" className="mt-2 h-20 object-contain" />}
                          </div>
                          <div>
                            <label className="block font-bold mb-2">PHOTO</label>
                            <input type="file" accept="image/*" onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                // const reader = new FileReader();
                                // reader.onloadend = () => {
                                //   const newChildren = [...children];
                                //   newChildren[index].photo = reader.result;
                                //   setChildren(newChildren);
                                //   const newPreviews = [...childrenPreviews];
                                //   if (!newPreviews[index]) newPreviews[index] = {};
                                //   newPreviews[index].photo = reader.result;
                                //   setChildrenPreviews(newPreviews);
                                //   localStorage.setItem('children', JSON.stringify(newChildren));
                                // };
                                // reader.readAsDataURL(file);

                                const previewUrl = URL.createObjectURL(file); 

                                const newFiles = [...childrenFiles];
                                if (!newFiles[index]) {
                                  newFiles[index] = {};
                                } 
                                if (newFiles[index].photo) {
                                  URL.revokeObjectURL(newFiles[index].photo);
                                }
                                newFiles[index].photo = file;
                                setChildrenFiles(newFiles);

                                const newPreviews = [...childrenPreviews];
                                if (!newPreviews[index]) {
                                  newPreviews[index] = {};
                                } 
                                if (newPreviews[index].photo) {
                                  URL.revokeObjectURL(newPreviews[index].photo);
                                }
                                newPreviews[index].photo = previewUrl;
                                setChildrenPreviews(newPreviews);

                                localforage.setItem('childrenFiles', newFiles);
                              }
                            }} className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none" />
                            {childrenPreviews[index]?.photo && <img src={childrenPreviews[index].photo} alt="Aperçu" className="mt-2 h-20 object-contain" />}
                          </div>
                          <div>
                            <label className="block font-bold mb-2">DOCUMENT CMU</label>
                            <div className="flex gap-4 mb-2">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name={`childCmuDocType_${index}`}
                                  value="carte"
                                  checked={(childrenCmuDocType[index] || 'carte') === 'carte'}
                                  onChange={() => {
                                    const updated = [...childrenCmuDocType];
                                    updated[index] = 'carte';
                                    setChildrenCmuDocType(updated);
                                  }}
                                />
                                <span>Carte CMU</span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name={`childCmuDocType_${index}`}
                                  value="recu"
                                  checked={(childrenCmuDocType[index] || 'carte') === 'recu'}
                                  onChange={() => {
                                    const updated = [...childrenCmuDocType];
                                    updated[index] = 'recu';
                                    setChildrenCmuDocType(updated);
                                  }}
                                />
                                <span>Reçu d'enrôlement</span>
                              </label>
                            </div>
                            <input type="file" accept="image/*" onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                // const reader = new FileReader();
                                // reader.onloadend = () => {
                                //   const newChildren = [...children];
                                //   newChildren[index].carteCmu = reader.result;
                                //   setChildren(newChildren);
                                //   const newPreviews = [...childrenPreviews];
                                //   if (!newPreviews[index]) newPreviews[index] = {};
                                //   newPreviews[index].carteCmu = reader.result;
                                //   setChildrenPreviews(newPreviews);
                                //   localStorage.setItem('children', JSON.stringify(newChildren));
                                // };
                                // reader.readAsDataURL(file);

                                const previewUrl = URL.createObjectURL(file); 

                                const newFiles = [...childrenFiles];
                                if (!newFiles[index]) {
                                  newFiles[index] = {};
                                } 
                                if (newFiles[index].carteCmu) {
                                  URL.revokeObjectURL(newFiles[index].carteCmu);
                                }
                                newFiles[index].carteCmu = file;
                                setChildrenFiles(newFiles);

                                const newPreviews = [...childrenPreviews];
                                if (!newPreviews[index]) {
                                  newPreviews[index] = {};
                                } 
                                if (newPreviews[index].carteCmu) {
                                  URL.revokeObjectURL(newPreviews[index].carteCmu);
                                }
                                newPreviews[index].carteCmu = previewUrl;
                                setChildrenPreviews(newPreviews);

                                localforage.setItem('childrenFiles', newFiles);
                              }
                            }} className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none" />
                            {childrenPreviews[index]?.carteCmu && (
                              <img
                                src={childrenPreviews[index].carteCmu}
                                alt={(childrenCmuDocType[index] || 'carte') === 'recu' ? "Reçu d'enrôlement" : "Carte CMU"}
                                className="mt-2 h-20 object-contain"
                              />
                            )}
                          </div>

                          {/* Champ Attestation de fréquentation - affiché uniquement si l'enfant a plus de 18 ans */}
                          {childOverAge && (
                            <div className="md:col-span-2">
                              <label className="block font-bold mb-2 text-amber-700">
                                ATTESTATION DE FRÉQUENTATION *
                                <span className="text-sm font-normal text-gray-600 ml-2">(Obligatoire pour les enfants de plus de 18 ans)</span>
                              </label>
                              <input type="file" accept="image/*,.pdf" onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  // const reader = new FileReader();
                                  // reader.onloadend = () => {
                                  //   const newChildren = [...children];
                                  //   newChildren[index].attestationFrequentation = reader.result;
                                  //   setChildren(newChildren);
                                  //   const newPreviews = [...childrenPreviews];
                                  //   if (!newPreviews[index]) newPreviews[index] = {};
                                  //   newPreviews[index].attestationFrequentation = reader.result;
                                  //   setChildrenPreviews(newPreviews);
                                  //   localStorage.setItem('children', JSON.stringify(newChildren));
                                  //   if (errors[`childDob${index}`]) {
                                  //     setErrors(prev => ({...prev, [`childDob${index}`]: ''}));
                                  //   }
                                  // };
                                  // reader.readAsDataURL(file);

                                  const previewUrl = URL.createObjectURL(file); 

                                  const newFiles = [...childrenFiles];
                                  if (!newFiles[index]) {
                                    newFiles[index] = {};
                                  } 
                                  if (newFiles[index].attestationFrequentation) {
                                    URL.revokeObjectURL(newFiles[index].attestationFrequentation);
                                  }
                                  newFiles[index].attestationFrequentation = file;
                                  setChildrenFiles(newFiles);

                                  const newPreviews = [...childrenPreviews];
                                  if (!newPreviews[index]) {
                                    newPreviews[index] = {};
                                  } 
                                  if (newPreviews[index].attestationFrequentation) {
                                    URL.revokeObjectURL(newPreviews[index].attestationFrequentation);
                                  }
                                  newPreviews[index].attestationFrequentation = previewUrl;
                                  setChildrenPreviews(newPreviews);

                                  localforage.setItem('childrenFiles', newFiles);

                                  if (errors[`childDob${index}`]) {
                                    setErrors(prev => ({...prev, [`childDob${index}`]: ''}));
                                  }
                                }
                              }} className="w-full border-2 border-amber-400 rounded-2xl px-4 py-2 focus:border-amber-600 focus:outline-none" />
                              {childrenPreviews[index]?.attestationFrequentation && (
                                <p className="mt-2 text-sm text-green-600 font-medium">✓ Attestation de fréquentation ajoutée</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </form>

            <div className="text-center mt-8 flex justify-center gap-4 pt-6 border-t">
              <Link
                to="/step-2"
                className="rounded-full border border-gray-300 px-6 py-3 text-gray-700 flex items-center gap-2"
              >
                ← Retour
              </Link>
              <button
                onClick={handleContinue}
                className="rounded-full bg-primary px-8 py-3 text-secondary font-medium flex items-center gap-2"
              >
                Continuer →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
